import { toast } from '@iguanads/react'
import * as Sentry from '@sentry/react'
import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { signOut } from 'firebase/auth'

import { env } from '@/env'
import { getFirebaseAuth } from '@/lib/firebase'

export const api = axios.create({
  baseURL: env.VITE_API_URL,
  timeout: 120000, // Increasing the timeout to 120 seconds to handle requests when the user is in the driver app
  // timeout: 60000,
})

let capturedAuthorizationToken: string | undefined

export const getCapturedAuthorizationToken = () => {
  if (!capturedAuthorizationToken) {
    const authHeader = api.defaults.headers.Authorization as string
    if (authHeader) {
      capturedAuthorizationToken = authHeader.replace('Bearer ', '')
    }
  }
  return capturedAuthorizationToken
}

const onRequest = (config: InternalAxiosRequestConfig) => {
  const authHeader =
    config.headers?.Authorization || config.headers?.authorization
  if (authHeader && !capturedAuthorizationToken) {
    capturedAuthorizationToken =
      typeof authHeader === 'string'
        ? authHeader.replace('Bearer ', '')
        : authHeader
  }
  return config
}

api.interceptors.request.use(onRequest)

export type ErrorResponseType = {
  statusCode: number
  message: string
  error: string
}

// Ends the session for real by signing out of Firebase, whose own persistence
// is where the auth state lives. Wiping localStorage instead left the Firebase
// session alive and threw away device preferences (theme, date range, push
// token) that have nothing to do with the session.
export const handleUnauthorizedSession = () => {
  api.defaults.headers.Authorization = ''
  signOut(getFirebaseAuth())
    .catch((error) => Sentry.captureException(error))
    .finally(() => {
      window.location.pathname = '/sign-in'
    })
}

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _isAuthRetry?: boolean
}

let pendingTokenRefresh: Promise<string | null> | null = null

// A burst of requests can all come back 401 at once; they share one forced
// refresh instead of each asking Firebase for a new token.
const refreshAuthorizationToken = () => {
  if (!pendingTokenRefresh) {
    const currentUser = getFirebaseAuth()?.currentUser
    pendingTokenRefresh = (
      currentUser ? currentUser.getIdToken(true) : Promise.resolve(null)
    )
      .catch(() => null)
      .finally(() => {
        pendingTokenRefresh = null
      })
  }
  return pendingTokenRefresh
}

// A 401 can just mean the token went stale before the periodic refresh got
// to it, so it gets one refresh and one retry before the session is ended.
const retryWithFreshToken = async (config?: RetriableRequestConfig) => {
  if (!config || config._isAuthRetry) return null

  const token = await refreshAuthorizationToken()
  if (!token) return null

  api.defaults.headers.Authorization = `Bearer ${token}`
  config._isAuthRetry = true
  config.headers.Authorization = `Bearer ${token}`
  return api.request(config)
}

// The /500 page shares AuthProvider with the rest of the app, so landing on
// it re-runs the same merchant-fetching flow that failed in the first place.
// If the backend keeps returning 500 for that request, redirecting again on
// every retry reloads /500 forever. Only navigate when we're not there yet.
export const shouldRedirectToServerErrorPage = () =>
  window.location.pathname !== '/500'

export const onResponseError = async (
  error: AxiosError
): Promise<AxiosResponse> => {
  // Only a 401 speaks for the session. A 403 means this user may not touch
  // this one resource, so it falls through to the caller like any other error.
  if (error?.response?.status === 401) {
    const retried = await retryWithFreshToken(error.config)
    if (retried) return retried

    handleUnauthorizedSession()

    // An expired session is an expected flow, not an application error. The
    // page is already navigating away, so settle nothing: rejecting here left
    // an orphan rejection behind on every callsite without a .catch.
    return new Promise(() => {})
  }

  if (error?.response?.status === 500) {
    if (shouldRedirectToServerErrorPage()) {
      window.location.pathname = '/500'
    }

    // The page is already navigating away to the error page (or already on
    // it), exactly like the 401 case above. Falling through to the
    // generic reject let this race with AuthContext's catch-all error
    // handling, which was signing the user out on every 500 instead of just
    // showing the error page.
    return new Promise(() => {})
  }

  if (error?.code === 'ECONNABORTED') {
    toast({
      title: 'Error',
      message: 'Request timeout, please try again.',
    })
    return Promise.reject(error)
  }

  toast({
    title: 'Error',
    message:
      (error?.response?.data as Record<string, string>)?.message ||
      (error?.response?.data as Record<string, string>)?.error ||
      'Ops, something went wrong.',
  })

  // Reject with the AxiosError itself. Rejecting with `response.data` handed
  // back a plain object, which Sentry cannot attach a stacktrace to and groups
  // under a single `<unknown>` title. The body stays available on
  // `error.response.data`.
  return Promise.reject(error)
}

api.interceptors.response.use((response) => response, onResponseError)
